import * as React from "react";

/**
 * Combine two refs into one, matching `@salt-ds/core`'s `useForkRef`.
 *
 * A component that needs an element for itself AND must forward it to a caller has two refs and
 * one `ref` slot. This merges them.
 *
 * Memoised on the two inputs so the returned callback is stable: an inline `(node) => {...}`
 * ref is a NEW function every render, and React responds by calling it with `null` and then the
 * node again on each one — which resets any state derived from it, every render.
 */
export function useForkRef<T>(
  refA: React.Ref<T> | null | undefined,
  refB: React.Ref<T> | null | undefined,
): React.RefCallback<T> | null {
  return React.useMemo(() => {
    if (refA == null && refB == null) return null;
    return (value: T | null) => {
      setRef(refA, value);
      setRef(refB, value);
    };
  }, [refA, refB]);
}

function setRef<T>(ref: React.Ref<T> | null | undefined, value: T | null): void {
  if (typeof ref === "function") ref(value);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = value;
}
